import { z } from 'zod';

import {
    anchorChainIdSchema,
    assertAnchorVerifyingContract,
    bytes32HexSchema,
    eoaAddressSchema,
} from '@/lib/api/tradingSession/schemas';

const ownerBindingSchema = z
    .object({
        nonce: bytes32HexSchema,
        issuedAt: z.number().int().nonnegative(),
        expiresAt: z.number().int().positive(),
    })
    .strict();

export const ownerBindingProofSchema = ownerBindingSchema
    .extend({
        signature: z.string().regex(/^0x[0-9a-fA-F]+$/u),
    })
    .strict();

const ownerBindingField = <
    const Name extends string,
    const Type extends string,
>(
        name: Name,
        type: Type,
    ) => z.object({ name: z.literal(name), type: z.literal(type) }).strict();

const ownerBindingTypedDataSchema = z
    .object({
        domain: z
            .object({
                name: z.literal('SnappyOwnerBinding'),
                version: z.literal('1'),
                // the anchor the wallet is being provisioned on; the manager
                // below must be exactly the one this app pins for that chain.
                chainId: anchorChainIdSchema,
                verifyingContract: eoaAddressSchema,
            })
            .strict()
            .superRefine((domain, context) => {
                if (
                    !assertAnchorVerifyingContract(
                        domain.chainId,
                        domain.verifyingContract,
                    )
                ) {
                    context.addIssue({
                        code: 'custom',
                        path: ['verifyingContract'],
                        message: 'Unexpected authorization registry contract',
                    });
                }
            }),
        types: z
            .object({
                OwnerBinding: z.tuple([
                    ownerBindingField('owner', 'address'),
                    ownerBindingField('manager', 'address'),
                    ownerBindingField('intentHash', 'bytes32'),
                    ownerBindingField('accountHashesHash', 'bytes32'),
                    ownerBindingField('nonce', 'bytes32'),
                    ownerBindingField('issuedAt', 'uint64'),
                    ownerBindingField('expiresAt', 'uint64'),
                ]),
            })
            .strict(),
        primaryType: z.literal('OwnerBinding'),
        message: z
            .object({
                owner: eoaAddressSchema,
                manager: eoaAddressSchema,
                intentHash: bytes32HexSchema,
                accountHashesHash: bytes32HexSchema,
                nonce: bytes32HexSchema,
                issuedAt: z.number().int().nonnegative(),
                expiresAt: z.number().int().positive(),
            })
            .strict(),
    })
    .strict()
    .superRefine((typedData, context) => {
        // `manager` is a SIGNED field, so it has to be pinned to the same
        // anchor manager as the domain rather than merely well-formed.
        if (
            !assertAnchorVerifyingContract(
                typedData.domain.chainId,
                typedData.message.manager,
            )
        ) {
            context.addIssue({
                code: 'custom',
                path: ['message', 'manager'],
                message: 'Unexpected authorization registry manager',
            });
        }
    });

export const authorizationRegistryChallengeSchema = z
    .object({
        owner: eoaAddressSchema,
        accountHash: bytes32HexSchema,
        ownerBinding: ownerBindingSchema,
        typedData: ownerBindingTypedDataSchema,
        // echoed by an anchor-aware backend; absent from an older one.
        anchorChainId: anchorChainIdSchema.optional(),
    })
    .strict();

export const authorizationRegistryProvisionSchema = z
    .object({
        owner: eoaAddressSchema,
        authorizationWallet: eoaAddressSchema,
        accountHash: bytes32HexSchema,
        anchorChainId: anchorChainIdSchema.optional(),
    })
    .strict();

export const associationSignatureSchema = z
    .object({
        signer: eoaAddressSchema,
        issued: z.string().regex(/^\d+$/u),
        expiry: z.string().regex(/^\d+$/u),
        digest: bytes32HexSchema,
        signature: z.string().regex(/^0x[0-9a-fA-F]+$/u),
    })
    .strict();

export type OwnerBinding = z.infer<typeof ownerBindingSchema>;
export type OwnerBindingProof = z.infer<typeof ownerBindingProofSchema>;
export type OwnerBindingTypedData = z.infer<
    typeof ownerBindingTypedDataSchema
>;
export type AuthorizationRegistryChallenge = z.infer<
    typeof authorizationRegistryChallengeSchema
>;
export type AuthorizationRegistryProvision = z.infer<
    typeof authorizationRegistryProvisionSchema
>;
export type AssociationSignature = z.infer<typeof associationSignatureSchema>;
